import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import {
  ArrowLeft, ArrowRight, ShieldCheck, CheckCircle2, AlertTriangle,
  Database, Image as ImageIcon, Package, Loader2, Upload, RefreshCw,
  Download, Zap,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import {
  adminListBackups,
  adminPreflightFolderImport,
  adminRetryFolderStorage,
  type PreflightReport,
  type ImportReport,
} from "@/lib/backups.functions";

export const Route = createFileRoute("/_authenticated/admin/migrate")({
  component: MigrateWizard,
});


type Step = 1 | 2 | 3;
type SourceKind = "folder" | "zip" | null;
type LogEntry = { ts: string; msg: string; kind: "info" | "ok" | "err" };

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const CHECKSUM_STORAGE_KEY = "lovable.migration.lastExportChecksum";

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function classifyError(err: unknown): { transient: boolean; friendly: string } {
  const raw = err instanceof Error ? err.message : String(err);
  const msg = raw.toLowerCase();
  if (msg.includes("networkerror") || msg.includes("failed to fetch") || msg.includes("network error") || msg.includes("econn") || msg.includes("etimedout") || msg.includes("timeout") || msg.includes("socket")) {
    return { transient: true, friendly: "Network issue talking to the server." };
  }
  const code = raw.match(/\b(408|425|429|500|502|503|504|507|509)\b/)?.[1];
  if (code) return { transient: true, friendly: `Server temporarily unavailable (HTTP ${code}).` };
  if (msg.includes("storage") && (msg.includes("quota") || msg.includes("temporar"))) {
    return { transient: true, friendly: "Storage backend hiccup." };
  }
  return { transient: false, friendly: raw };
}

async function withRetry<T>(
  label: string,
  fn: () => Promise<T>,
  log: (m: string, k?: "info" | "ok" | "err") => void,
  maxRetries = 4,
): Promise<T> {
  let delay = 1000;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (e) {
      const { transient, friendly } = classifyError(e);
      if (attempt === maxRetries || !transient) {
        log(`${label} failed: ${friendly}${transient ? " — gave up after retries. Check your connection and try again." : ""}`, "err");
        throw e instanceof Error ? e : new Error(String(e));
      }
      log(`${label}: ${friendly} Retrying ${attempt + 1}/${maxRetries} in ${delay / 1000}s…`, "info");
      await new Promise((r) => setTimeout(r, delay));
      delay *= 2;
    }
  }
  throw new Error("unreachable");
}

function MigrateWizard() {
  const { isAdmin } = useAuth();
  const listFn = useServerFn(adminListBackups);
  const preflightFn = useServerFn(adminPreflightFolderImport);
  const retryFn = useServerFn(adminRetryFolderStorage);
  
  const fileRef = useRef<HTMLInputElement>(null);
  const [oneClickStatus, setOneClickStatus] = useState<string>("");
  const [oneClickErr, setOneClickErr] = useState<string | null>(null);
  const [oneClickBusy, setOneClickBusy] = useState(false);
  const [oneClickProgress, setOneClickProgress] = useState(0);
  const [oneClickLogs, setOneClickLogs] = useState<LogEntry[]>([]);
  const [oneClickBytes, setOneClickBytes] = useState<{ loaded: number; total: number }>({ loaded: 0, total: 0 });

  function pushLog(msg: string, kind: LogEntry["kind"] = "info") {
    setOneClickLogs((l) => [...l, { ts: new Date().toLocaleTimeString(), msg, kind }]);
  }


  const [oneClickChecksum, setOneClickChecksum] = useState<string | null>(null);

  async function oneClickExport() {
    setOneClickBusy(true); setOneClickErr(null); setOneClickLogs([]); setOneClickProgress(0); setOneClickBytes({ loaded: 0, total: 0 }); setOneClickChecksum(null);
    setOneClickStatus("Creating full backup…");
    const tick = (pct: number, msg: string, kind: LogEntry["kind"] = "info") => {
      setOneClickProgress(pct);
      pushLog(msg, kind);
    };
    try {
      tick(5, "Starting full backup (tables + storage + auth users)…");

      // Use the SSE streaming endpoint so long backups don't hit the
      // request timeout of a plain server function (which used to stall
      // this flow around 55%).
      const sess0 = await supabase.auth.getSession();
      const token0 = sess0.data.session?.access_token;
      if (!token0) throw new Error("Sign in required");

      const b = await withRetry("Server backup", async () => {
        const resp = await fetch("/api/admin/backup-stream", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token0}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            includeStorage: true,
            includeAuthUsers: true,
            notes: "One-click migration export",
          }),
        });
        if (!resp.ok || !resp.body) {
          throw new Error(`HTTP ${resp.status} ${await resp.text().catch(() => "")}`.trim());
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let summary: any = null;
        let lastErr: string | null = null;

        // rough phase → progress mapping so the bar keeps moving
        const phasePct: Record<string, number> = {
          starting: 8,
          tables: 15,
          tables_done: 30,
          auth: 33,
          auth_done: 36,
          storage_list: 38,
          storage_file: 45,
          storage_done: 55,
          manifest: 57,
          metadata: 59,
          done: 60,
        };
        let lastPct = 5;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const events = buf.split("\n\n");
          buf = events.pop() ?? "";
          for (const raw of events) {
            const evLine = raw.match(/^event:\s*(.+)$/m)?.[1]?.trim();
            const dataLine = raw.match(/^data:\s*(.+)$/m)?.[1];
            if (!evLine || !dataLine) continue;
            let payload: any = {};
            try { payload = JSON.parse(dataLine); } catch { /* ignore */ }
            if (evLine === "progress") {
              const p = payload.phase as string | undefined;
              const target = (p && phasePct[p]) || lastPct;
              if (target > lastPct) {
                lastPct = target;
                setOneClickProgress(lastPct);
              }
              if (payload.message) pushLog(payload.message);
            } else if (evLine === "done") {
              summary = payload.backup;
            } else if (evLine === "error") {
              lastErr = payload.message || "Backup failed";
            }
          }
        }
        if (lastErr) throw new Error(lastErr);
        if (!summary) throw new Error("Server closed the stream before finishing the backup");
        return summary;
      }, pushLog);

      tick(60, `Backup ready: ${b.file_path} (${formatBytes(b.size_bytes)})`, "ok");


      tick(65, "Authorizing download…");
      const sess = await supabase.auth.getSession();
      const token = sess.data.session?.access_token;
      if (!token) throw new Error("Sign in required");

      const folder = b.file_path.replace(/\/$/, "");
      const url = `/api/admin/download-folder-zip?folder=${encodeURIComponent(folder)}&token=${encodeURIComponent(token)}`;
      tick(70, "Streaming ZIP from server…");

      const blob = await withRetry("ZIP download", async () => {
        const resp = await fetch(url);
        if (!resp.ok) {
          const text = await resp.text().catch(() => "");
          throw new Error(`HTTP ${resp.status} ${text}`.trim());
        }
        const total = Number(resp.headers.get("content-length") || 0);
        setOneClickBytes({ loaded: 0, total });
        const reader = resp.body?.getReader();
        const chunks: Uint8Array[] = [];
        let loaded = 0;
        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) {
              chunks.push(value);
              loaded += value.length;
              setOneClickBytes({ loaded, total });
              if (total > 0) {
                const pct = 70 + Math.floor((loaded / total) * 23);
                setOneClickProgress(Math.min(pct, 93));
              }
            }
          }
        }
        return new Blob(chunks as BlobPart[], { type: "application/zip" });
      }, pushLog);

      tick(95, "Computing SHA-256 checksum…");
      const buf = await blob.arrayBuffer();
      const checksum = await sha256Hex(buf);
      setOneClickChecksum(checksum);
      try { localStorage.setItem(CHECKSUM_STORAGE_KEY, checksum); } catch { /* ignore */ }

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl; a.download = `${folder.split("/").pop()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);

      tick(100, `Downloaded ${formatBytes(blob.size)}. SHA-256: ${checksum.slice(0, 16)}…`, "ok");
      pushLog("Save this checksum — paste it in the new project to verify the ZIP before import.", "ok");
      setOneClickStatus("Done.");
    } catch (e: any) {
      const { friendly } = classifyError(e);
      setOneClickErr(friendly);
      setOneClickStatus("");
    } finally {
      setOneClickBusy(false);
    }
  }



  const [step, setStep] = useState<Step>(1);
  const [sourceKind, setSourceKind] = useState<SourceKind>(null);
  const [folder, setFolder] = useState<string>("");
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [zipChecksum, setZipChecksum] = useState<string | null>(null);
  const [zipChecksumBusy, setZipChecksumBusy] = useState(false);
  const [expectedChecksum, setExpectedChecksum] = useState<string>("");
  const [checksumVerified, setChecksumVerified] = useState<null | boolean>(null);
  const [preflight, setPreflight] = useState<PreflightReport | null>(null);
  const [preflightErr, setPreflightErr] = useState<string | null>(null);
  const [importReport, setImportReport] = useState<ImportReport | null>(null);
  const [importErr, setImportErr] = useState<string | null>(null);
  const [importLogs, setImportLogs] = useState<LogEntry[]>([]);
  const [importPhase, setImportPhase] = useState<string>("");
  const [failedPhase, setFailedPhase] = useState<string | null>(null);
  const [completedPhases, setCompletedPhases] = useState<("tables" | "storage" | "users")[]>([]);
  const [running, setRunning] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  function pushImportLog(msg: string, kind: LogEntry["kind"] = "info") {
    setImportLogs((l) => [...l, { ts: new Date().toLocaleTimeString(), msg, kind }]);
  }
  function classifyImportError(e: any): string {
    const m = (e?.message ?? String(e)) || "Unknown error";
    if (/network|fetch|ECONN|ETIMEDOUT/i.test(m)) return `${m} — network issue, check connection and retry`;
    if (/401|unauthor/i.test(m)) return `${m} — sign in again, your session may have expired`;
    if (/403|forbidden/i.test(m)) return `${m} — admin privileges required`;
    if (/413|too large/i.test(m)) return `${m} — ZIP exceeds server limit, split the backup`;
    if (/429/i.test(m)) return `${m} — rate limited, wait a minute and retry`;
    if (/5\d\d/.test(m)) return `${m} — server error, retry; if it persists check function logs`;
    if (/checksum|RESTORE/i.test(m)) return `${m} — verify the checksum and confirm word`;
    return m;
  }


  const backups = useQuery({
    queryKey: ["admin", "backups", "list"],
    queryFn: () => listFn(),
    enabled: isAdmin,
  });

  const folderBackups = (backups.data ?? []).filter((b) => b.file_path.endsWith("/"));

  const mPreflight = useMutation({
    mutationFn: async () => {
      setPreflightErr(null);
      if (sourceKind === "folder") {
        if (!folder) throw new Error("Pick a backup folder");
        return preflightFn({ data: { folder } });
      }
      if (sourceKind === "zip") {
        if (!zipFile) throw new Error("Pick a ZIP file");
        const sess = await supabase.auth.getSession();
        const token = sess.data.session?.access_token;
        if (!token) throw new Error("Sign in required");
        const resp = await fetch("/api/admin/preflight-zip", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/octet-stream" },
          body: zipFile,
        });
        if (!resp.ok) throw new Error(await resp.text());
        return (await resp.json()) as PreflightReport;
      }
      throw new Error("Choose a source");
    },
    onSuccess: (r) => { setPreflight(r); setStep(2); },
    onError: (e: any) => setPreflightErr(e?.message ?? String(e)),
  });

  async function runImport(skipPhases?: ("tables" | "storage" | "users")[]) {
    setRunning(true); setImportErr(null); setImportReport(null);
    if (!skipPhases || skipPhases.length === 0) {
      setImportLogs([]); setCompletedPhases([]);
    } else {
      pushImportLog(`↻ Resuming import — skipping: ${skipPhases.join(", ")}`, "info");
    }
    setFailedPhase(null);
    setImportPhase("validating");
    const t0 = performance.now();
    const skipSet = new Set(skipPhases ?? []);
    const phase = (name: string, msg: string) => {
      setImportPhase(name);
      pushImportLog(`▶ ${msg}`);
      return performance.now();
    };
    const done = (started: number, msg: string, phaseName?: "tables" | "storage" | "users") => {
      const secs = ((performance.now() - started) / 1000).toFixed(2);
      pushImportLog(`✓ ${msg} (${secs}s)`, "ok");
      if (phaseName) setCompletedPhases((p) => Array.from(new Set([...p, phaseName])));
    };
    try {
      const tValidate = phase("validating", "Validating source & session");
      const sess = await supabase.auth.getSession();
      const token = sess.data.session?.access_token;
      if (!token) throw new Error("401 Unauthorized: sign in required");
      if (sourceKind === "zip" && !zipFile) throw new Error("No ZIP selected");
      if (sourceKind === "folder" && !folder) throw new Error("No folder selected");
      done(tValidate, "Source validated");

      const tUpload = phase("uploading", sourceKind === "zip" ? "Uploading ZIP & restoring on server" : "Restoring backup folder on server");
      let report: ImportReport;
      if (sourceKind === "zip" && zipFile) {
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/octet-stream",
          "x-confirm-restore": "RESTORE",
        };
        if (skipPhases && skipPhases.length) headers["x-skip-phases"] = skipPhases.join(",");
        const resp = await fetch("/api/admin/import-backup", { method: "POST", headers, body: zipFile });
        if (!resp.ok) throw new Error(`${resp.status} ${await resp.text()}`);
        report = await resp.json();
      } else {
        const { adminImportBackup } = await import("@/lib/backups.functions");
        report = await adminImportBackup({ data: { sourcePath: folder, confirm: "RESTORE", skipPhases } });
      }
      done(tUpload, "Server returned report");

      if (!skipSet.has("tables")) {
        const tTables = phase("tables", `Restored ${report.tables_restored} tables / ${report.rows_restored.toLocaleString()} rows`);
        done(tTables, "Tables phase complete", "tables");
      } else pushImportLog("⤼ Tables phase skipped (resume)", "info");

      if (!skipSet.has("storage")) {
        const tStorage = phase("storage", `Restored ${report.storage_files_restored.toLocaleString()} files (${formatBytes(report.storage_bytes_restored)})`);
        if (report.storage_files_missing.length > 0) {
          pushImportLog(`⚠ ${report.storage_files_missing.length} storage file(s) missing — use Retry`, "err");
        }
        done(tStorage, "Storage phase complete", "storage");
      } else pushImportLog("⤼ Storage phase skipped (resume)", "info");

      if (!skipSet.has("users")) {
        const tUsers = phase("users", `Processed ${report.auth_users_seen} auth user(s)`);
        done(tUsers, "Users phase complete", "users");
      } else pushImportLog("⤼ Users phase skipped (resume)", "info");

      if (report.errors.length > 0) {
        pushImportLog(`⚠ ${report.errors.length} non-fatal error(s) — see report below`, "err");
      }
      const total = ((performance.now() - t0) / 1000).toFixed(2);
      pushImportLog(`✅ Migration finished in ${total}s`, "ok");
      setImportPhase("done");
      setImportReport(report);
    } catch (e: any) {
      const msg = classifyImportError(e);
      pushImportLog(`✗ Failed during "${importPhase}": ${msg}`, "err");
      setImportErr(msg);
      setFailedPhase(importPhase);
    } finally {
      setRunning(false);
    }
  }



  async function retryStorage() {
    if (!folder) return;
    setRunning(true); setImportErr(null);
    try {
      const r = await retryFn({ data: { folder, confirm: "RETRY" } });
      setImportReport(r);
    } catch (e: any) {
      setImportErr(e?.message ?? String(e));
    } finally {
      setRunning(false);
    }
  }

  if (!isAdmin) {
    return <div className="p-8">Admin access required.</div>;
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl p-6">
        <Link to="/admin/backups" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="size-4" /> Back to Database
        </Link>

        <header className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="size-6" /> Remix Migration Wizard
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Move a complete project — tables, storage files, auth metadata — into this database. Preflight checks every table and bucket before you commit.
          </p>
        </header>

        <section className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 mb-6">
          <div className="flex items-start gap-3">
            <div className="size-10 rounded-xl bg-emerald-600 text-white grid place-items-center shrink-0">
              <Zap className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-base">One-Click Migration Export</h2>
              <p className="text-xs text-slate-600 mt-0.5">
                Builds a full bundle (every table + storage buckets + auth users) and downloads it as a single ZIP. Upload that ZIP into the new project's Migration Wizard to restore.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  onClick={oneClickExport}
                  disabled={oneClickBusy}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  {oneClickBusy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                  Export Full Project (ZIP)
                </button>
                {oneClickStatus && <span className="text-xs text-slate-600">{oneClickStatus}</span>}
              </div>
              {(oneClickBusy || oneClickProgress > 0) && (
                <div className="mt-3">
                  <div className="h-2 w-full rounded-full bg-emerald-100 overflow-hidden">
                    <div
                      className="h-full bg-emerald-600 transition-all duration-300"
                      style={{ width: `${oneClickProgress}%` }}
                    />
                  </div>
                  <div className="mt-1 flex justify-between text-[11px] text-slate-600 tabular-nums">
                    <span>{oneClickProgress}%</span>
                    {oneClickBytes.total > 0 && (
                      <span>{formatBytes(oneClickBytes.loaded)} / {formatBytes(oneClickBytes.total)}</span>
                    )}
                  </div>
                </div>
              )}
              {oneClickLogs.length > 0 && (
                <div className="mt-3 rounded-lg border bg-slate-950 text-slate-100 p-3 max-h-48 overflow-auto font-mono text-[11px] leading-relaxed">
                  {oneClickLogs.map((l, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="text-slate-500 shrink-0">{l.ts}</span>
                      <span className={l.kind === "err" ? "text-rose-400" : l.kind === "ok" ? "text-emerald-400" : "text-slate-200"}>
                        {l.kind === "err" ? "✗" : l.kind === "ok" ? "✓" : "›"} {l.msg}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {oneClickErr && (
                <div className="mt-2 text-xs text-rose-700 flex items-center gap-1"><AlertTriangle className="size-3.5" /> {oneClickErr}</div>
              )}
              {oneClickChecksum && (
                <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                  <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5" /> Export checksum (SHA-256)
                  </div>
                  <div className="mt-1 font-mono text-[11px] break-all text-emerald-900 select-all">{oneClickChecksum}</div>
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={() => { navigator.clipboard?.writeText(oneClickChecksum); }}
                      className="text-[11px] rounded border border-emerald-400 bg-white px-2 py-1 hover:bg-emerald-100"
                    >Copy</button>
                    <span className="text-[11px] text-emerald-900/80 self-center">Paste this in the new project's wizard to verify the ZIP before import.</span>
                  </div>
                </div>
              )}

            </div>
          </div>
        </section>



        <ol className="flex items-center gap-2 text-xs mb-6">
          {([1, 2, 3] as Step[]).map((n, i) => (
            <li key={n} className="flex items-center gap-2">
              <span className={`size-7 rounded-full grid place-items-center font-semibold ${step >= n ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>{n}</span>
              <span className={step >= n ? "text-slate-900 font-medium" : "text-slate-500"}>
                {n === 1 ? "Source" : n === 2 ? "Preflight" : "Run Migration"}
              </span>
              {i < 2 && <ArrowRight className="size-3.5 text-slate-300 mx-1" />}
            </li>
          ))}
        </ol>

        {step === 1 && (
          <section className="rounded-2xl border bg-white p-5 space-y-5">
            <h2 className="font-semibold text-lg">Step 1 — Choose Source</h2>

            <div>
              <label className="block text-sm font-medium mb-2">Existing backup in this project</label>
              <select
                value={sourceKind === "folder" ? folder : ""}
                onChange={(e) => { setSourceKind("folder"); setFolder(e.target.value); setZipFile(null); }}
                className="w-full rounded-lg border px-3 py-2 text-sm"
              >
                <option value="">— select a folder backup —</option>
                {folderBackups.map((b) => (
                  <option key={b.id} value={b.file_path}>
                    {b.file_path} · {formatBytes(b.size_bytes)} · {new Date(b.created_at).toLocaleString()}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">Best when you exported on this same project and want to restore in place.</p>
            </div>

            <div className="text-center text-xs uppercase tracking-wide text-slate-400">— or —</div>

            <div>
              <label className="block text-sm font-medium mb-2">Upload a migration ZIP</label>
              <input
                ref={fileRef}
                type="file"
                accept=".zip,application/zip"
                onChange={async (e) => {
                  const f = e.target.files?.[0] ?? null;
                  setZipChecksum(null); setChecksumVerified(null);

                  if (f) {
                    setSourceKind("zip"); setZipFile(f); setFolder("");
                    setZipChecksumBusy(true);
                    try {
                      const buf = await f.arrayBuffer();
                      const sum = await sha256Hex(buf);
                      setZipChecksum(sum);
                    } catch (err: any) {
                      setPreflightErr(`Could not read file: ${err?.message ?? err}`);
                    } finally {
                      setZipChecksumBusy(false);
                    }
                  } else {
                    setZipFile(null);
                  }
                }}
                className="block w-full text-sm"
              />
              {zipFile && (
                <p className="text-xs text-slate-600 mt-1">{zipFile.name} · {formatBytes(zipFile.size)}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">Use a ZIP exported from another project's Database page.</p>

              {(zipChecksumBusy || zipChecksum) && (
                <div className="mt-3 rounded-lg border bg-slate-50 p-3 space-y-2">
                  <div className="text-xs font-semibold flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5" /> ZIP integrity check (SHA-256)
                  </div>
                  {zipChecksumBusy ? (
                    <div className="text-xs text-slate-600 flex items-center gap-1.5"><Loader2 className="size-3.5 animate-spin" /> Computing checksum…</div>
                  ) : (
                    <>
                      <div className="font-mono text-[11px] break-all text-slate-700 select-all">{zipChecksum}</div>
                      <label className="block text-[11px] font-medium text-slate-600">Paste expected checksum from the exporting project:</label>
                      <input
                        value={expectedChecksum}
                        onChange={(e) => { setExpectedChecksum(e.target.value.trim()); setChecksumVerified(null); }}
                        placeholder="sha256 hex from source project"
                        className="w-full rounded border px-2 py-1.5 text-xs font-mono"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => {
                            const ok = !!expectedChecksum && expectedChecksum.toLowerCase() === zipChecksum!.toLowerCase();
                            setChecksumVerified(ok);
                          }}
                          disabled={!expectedChecksum}
                          className="text-[11px] rounded bg-slate-900 text-white px-2.5 py-1 disabled:opacity-50"
                        >Verify</button>
                        {checksumVerified === true && (
                          <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="size-3.5" /> Verification successful — file matches source.</span>
                        )}
                        {checksumVerified === false && (
                          <span className="text-[11px] text-rose-700 inline-flex items-center gap-1"><AlertTriangle className="size-3.5" /> Checksum mismatch — re-export and try again. Import is blocked.</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>


            {preflightErr && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800 flex gap-2">
                <AlertTriangle className="size-4 shrink-0 mt-0.5" /> {preflightErr}
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => mPreflight.mutate()}
                disabled={mPreflight.isPending || (!folder && !zipFile)}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {mPreflight.isPending ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                Run Preflight Check
              </button>
            </div>
          </section>
        )}

        {step === 2 && preflight && (
          <section className="rounded-2xl border bg-white p-5 space-y-5">
            <h2 className="font-semibold text-lg flex items-center gap-2">
              <ShieldCheck className="size-5 text-emerald-600" /> Step 2 — Preflight Report
            </h2>

            <div className="grid sm:grid-cols-4 gap-3">
              <Stat label="Source" value={preflight.source_format.toUpperCase()} />
              <Stat label="Tables" value={String(preflight.tables_in_backup.length)} />
              <Stat label="Storage" value={formatBytes(preflight.storage_total_bytes)} />
              <Stat label="Auth Users" value={String(preflight.auth_users)} />
            </div>

            {preflight.warnings.length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                <div className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle className="size-4" /> Warnings</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {preflight.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {preflight.tables_missing_in_target.length > 0 && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-900">
                <div className="font-semibold mb-1">Tables in backup but missing from this target:</div>
                <div className="font-mono text-xs">{preflight.tables_missing_in_target.join(", ")}</div>
                <p className="mt-2 text-xs">Run the appropriate migrations on this project before importing, or rows for these tables will be skipped with errors.</p>
              </div>
            )}

            {preflight.schema_issues && preflight.schema_issues.length > 0 && (
              <div className={`rounded-lg p-3 text-sm border ${preflight.schema_ok ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}>
                <div className="font-semibold mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="size-4" />
                  Schema compatibility issues ({preflight.schema_issues.length} table{preflight.schema_issues.length === 1 ? "" : "s"})
                </div>
                <ul className="mt-1 space-y-1 text-xs">
                  {preflight.schema_issues.map((s) => (
                    <li key={s.table} className="font-mono">
                      <span className="font-semibold">{s.table}</span>
                      {s.missing_columns.length > 0 && <> · missing cols: {s.missing_columns.join(", ")}</>}
                      {s.pk_missing.length > 0 && <> · PK missing: {s.pk_missing.join(", ")} (target PK: {s.pk_target.join(", ")})</>}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs">
                  {preflight.schema_ok
                    ? "Extra columns in backup will be ignored on insert."
                    : "Primary key columns are missing in target — these rows cannot be upserted. Run target migrations first."}
                </p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Database className="size-4" /> Tables ({preflight.tables_in_backup.length})</h3>
                <div className="max-h-64 overflow-auto rounded-lg border text-xs">
                  <table className="w-full">
                    <tbody>
                      {preflight.tables_in_backup.map((t) => (
                        <tr key={t.name} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{t.name}</td>
                          <td className="px-2 py-1 text-right text-slate-500">{t.rows.toLocaleString()}</td>
                          <td className="px-2 py-1 text-right">
                            {preflight.tables_missing_in_target.includes(t.name)
                              ? <span className="text-rose-600">missing</span>
                              : <CheckCircle2 className="inline size-3.5 text-emerald-600" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><ImageIcon className="size-4" /> Buckets ({preflight.buckets_in_backup.length})</h3>
                <div className="max-h-64 overflow-auto rounded-lg border text-xs">
                  <table className="w-full">
                    <tbody>
                      {preflight.buckets_in_backup.length === 0 && (
                        <tr><td className="px-2 py-3 text-slate-500 text-center">No storage in backup</td></tr>
                      )}
                      {preflight.buckets_in_backup.map((b) => (
                        <tr key={b.name} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{b.name}</td>
                          <td className="px-2 py-1 text-slate-500">{b.isPublic ? "public" : "private"}</td>
                          <td className="px-2 py-1 text-right text-slate-500">{b.files.toLocaleString()} files</td>
                          <td className="px-2 py-1 text-right">
                            {preflight.buckets_missing_in_target.includes(b.name)
                              ? <span className="text-amber-600">will create</span>
                              : <CheckCircle2 className="inline size-3.5 text-emerald-600" />}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Dry-run plan */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="font-semibold text-sm mb-2 flex items-center gap-2">
                <ShieldCheck className="size-4 text-emerald-600" /> Dry-Run Plan — exactly what will happen
              </div>
              {(() => {
                const totalRows = preflight.tables_in_backup.reduce((s, t) => s + (t.rows || 0), 0);
                const totalFiles = preflight.buckets_in_backup.reduce((s, b) => s + (b.files || 0), 0);
                const writableTables = preflight.tables_in_backup.length - preflight.tables_missing_in_target.length;
                const newBuckets = preflight.buckets_missing_in_target.length;
                const plan: { ok: boolean; text: string }[] = [
                  { ok: true, text: `Take a pre-import snapshot of this project (safety net)` },
                  { ok: writableTables > 0, text: `Upsert ${totalRows.toLocaleString()} rows into ${writableTables} table(s)` },
                  { ok: preflight.tables_missing_in_target.length === 0, text: preflight.tables_missing_in_target.length === 0 ? `All ${preflight.tables_in_backup.length} backup tables exist in target` : `Skip ${preflight.tables_missing_in_target.length} table(s) missing in target` },
                  { ok: true, text: newBuckets > 0 ? `Create ${newBuckets} new bucket(s), then upload ${totalFiles.toLocaleString()} file(s)` : `Upload ${totalFiles.toLocaleString()} file(s) to existing buckets` },
                  { ok: true, text: `Process ${preflight.auth_users} auth user(s) (metadata only; passwords cannot transfer)` },
                ];
                return (
                  <ul className="text-xs space-y-1">
                    {plan.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        {p.ok ? <CheckCircle2 className="size-3.5 text-emerald-600 mt-0.5 shrink-0" /> : <AlertTriangle className="size-3.5 text-amber-600 mt-0.5 shrink-0" />}
                        <span>{p.text}</span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>



            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="text-sm text-slate-600 hover:text-slate-900">← Back</button>
              <button
                onClick={() => setStep(3)}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold"
              >
                Continue <ArrowRight className="size-4" />
              </button>
            </div>
          </section>
        )}

        {step === 3 && preflight && (
          <section className="rounded-2xl border bg-white p-5 space-y-4">
            <h2 className="font-semibold text-lg">Step 3 — Run Migration</h2>

            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
              <div className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle className="size-4" /> Destructive operation</div>
              An automatic pre-import snapshot is created first. Rows are upserted into existing tables; storage files are uploaded with upsert. Type <strong className="font-mono">RESTORE</strong> to confirm.
            </div>

            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESTORE"
              className="w-full rounded-lg border px-3 py-2 text-sm font-mono"
            />

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => runImport()}
                disabled={running || confirmText !== "RESTORE" || (sourceKind === "zip" && checksumVerified !== true)}
                title={sourceKind === "zip" && checksumVerified !== true ? "Verify the ZIP checksum in Step 1 before importing" : undefined}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
              >
                {running ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                Start Migration
              </button>
              {sourceKind === "zip" && checksumVerified !== true && (
                <span className="text-xs text-amber-700 self-center inline-flex items-center gap-1">
                  <AlertTriangle className="size-3.5" /> Verify ZIP checksum in Step 1 first.
                </span>
              )}
              {sourceKind === "folder" && importReport && (
                <button
                  onClick={retryStorage}
                  disabled={running}
                  className="inline-flex items-center gap-2 rounded-lg bg-white border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  {running ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Retry Missing Storage Files
                </button>
              )}
              <button onClick={() => setStep(2)} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2">← Back</button>
            </div>

            {importErr && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">{importErr}</div>
            )}

            {failedPhase && !running && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-2">
                <div className="font-semibold flex items-center gap-1.5">
                  <RefreshCw className="size-4" /> Resume from last completed phase
                </div>
                <div className="text-xs">
                  Failed during <span className="font-mono">{failedPhase}</span>.
                  Completed so far: {completedPhases.length ? completedPhases.join(", ") : "none"}.
                  Resume will skip completed phases and retry the rest.
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => runImport(completedPhases)}
                    disabled={running || completedPhases.length === 0}
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-700 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                  >
                    <RefreshCw className="size-3.5" /> Resume (skip {completedPhases.length || 0})
                  </button>
                  <button
                    onClick={() => runImport()}
                    disabled={running}
                    className="inline-flex items-center gap-2 rounded-lg bg-white border border-amber-300 px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
                  >
                    Restart from scratch
                  </button>
                </div>
              </div>
            )}

            {(running || importLogs.length > 0) && (
              <div className="rounded-lg border bg-slate-950 text-slate-100 p-3 text-xs font-mono max-h-72 overflow-auto">
                <div className="flex items-center justify-between mb-2 text-slate-400">
                  <span>Import logs {importPhase && `· phase: ${importPhase}`}</span>
                  {running && <Loader2 className="size-3 animate-spin" />}
                </div>
                {importLogs.map((l, i) => (
                  <div key={i} className={l.kind === "err" ? "text-rose-300" : l.kind === "ok" ? "text-emerald-300" : "text-slate-200"}>
                    <span className="text-slate-500">{l.ts}</span> {l.msg}
                  </div>
                ))}
              </div>
            )}


            {importReport && (
              <div className="rounded-lg border bg-slate-50 p-4 space-y-3">
                <div className="font-semibold flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="size-5" /> Migration Report
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <Stat label="Tables" value={String(importReport.tables_restored)} />
                  <Stat label="Rows" value={importReport.rows_restored.toLocaleString()} />
                  <Stat label="Files restored" value={importReport.storage_files_restored.toLocaleString()} />
                  <Stat label="Bytes" value={formatBytes(importReport.storage_bytes_restored)} />
                  <Stat label="Verified (sha256)" value={String(importReport.storage_files_verified)} />
                  <Stat label="Missing/failed" value={String(importReport.storage_files_missing.length)} />
                  <Stat label="Auth users seen" value={String(importReport.auth_users_seen)} />
                  <Stat label="Errors" value={String(importReport.errors.length)} />
                </div>
                {importReport.storage_files_missing.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer font-semibold text-rose-700">
                      {importReport.storage_files_missing.length} missing file(s) — use Retry above
                    </summary>
                    <ul className="mt-2 max-h-40 overflow-auto font-mono">
                      {importReport.storage_files_missing.slice(0, 200).map((m, i) => (
                        <li key={i}>{m.bucket}/{m.path}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {importReport.errors.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer font-semibold text-rose-700">{importReport.errors.length} error(s)</summary>
                    <ul className="mt-2 max-h-40 overflow-auto font-mono">
                      {importReport.errors.slice(0, 200).map((e, i) => (
                        <li key={i}>[{e.table}] {e.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}
